import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Link } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { useState, useEffect } from "react";

export default function AdminDeployments() {
  const [checkpointId, setCheckpointId] = useState("");
  const [notes, setNotes] = useState("");
  const [version, setVersion] = useState("1.0.0");
  const [fixNumber, setFixNumber] = useState(1);

  const { data: nextVersion } = trpc.system.getNextVersion.useQuery();
  const { data: history, isLoading, refetch } = trpc.system.getDeploymentHistory.useQuery({ limit: 50 });
  
  // Update version and fix number when nextVersion is loaded
  useEffect(() => {
    if (nextVersion) {
      setVersion(nextVersion.version);
      setFixNumber(nextVersion.fixNumber);
    }
  }, [nextVersion]);
  const logDeployment = trpc.system.logDeployment.useMutation({
    onSuccess: () => {
      toast.success("Deployment logged successfully!");
      setCheckpointId("");
      setNotes("");
      refetch();
    },
    onError: (error) => {
      toast.error(`Failed to log deployment: ${error.message}`);
    },
  });

  const handleLogDeployment = () => {
    if (!checkpointId.trim()) {
      toast.error("Please enter a checkpoint ID");
      return;
    }

    logDeployment.mutate({
      version,
      fixNumber,
      checkpointId: checkpointId.trim(),
      notes: notes.trim() || undefined,
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-background via-background to-blue-950/20">
      {/* Navigation Header */}
      <header className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Link href="/admin">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Admin
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-primary via-amber-300 to-primary bg-clip-text text-transparent">
              Deployment Log
            </h1>
          </div>
        </div>
      </header>

      <main className="flex-1 container mx-auto py-8 px-4">

        {/* Log New Deployment */}
        <Card className="mb-8 bg-gradient-to-br from-card to-purple-950/20 border-purple-500/20">
        <CardHeader>
          <CardTitle>Log New Deployment</CardTitle>
          <CardDescription>
            Record a new deployment to track version history
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Version</Label>
              <Input value={version} onChange={(e) => setVersion(e.target.value)} />
              <p className="text-xs text-muted-foreground mt-1">Auto-suggested, editable</p>
            </div>
            <div>
              <Label>Fix Number</Label>
              <Input type="number" value={fixNumber} onChange={(e) => setFixNumber(parseInt(e.target.value))} />
              <p className="text-xs text-muted-foreground mt-1">Auto-incremented</p>
            </div>
          </div>

          <div>
            <Label htmlFor="checkpointId">Checkpoint ID *</Label>
            <Input
              id="checkpointId"
              placeholder="e.g., 193f7296"
              value={checkpointId}
              onChange={(e) => setCheckpointId(e.target.value)}
            />
            <p className="text-sm text-muted-foreground mt-1">
              The checkpoint ID from the Manus platform (8 characters)
            </p>
          </div>

          <div>
            <Label htmlFor="notes">Deployment Notes (Optional)</Label>
            <Textarea
              id="notes"
              placeholder="e.g., Fixed iOS Safari crash, added AI learning system..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          <Button 
            onClick={handleLogDeployment} 
            disabled={logDeployment.isPending}
          >
            {logDeployment.isPending ? "Logging..." : "Log Deployment"}
          </Button>
        </CardContent>
      </Card>

      {/* Deployment History */}
      <Card>
        <CardHeader>
          <CardTitle>Deployment History</CardTitle>
          <CardDescription>
            All recorded deployments in chronological order
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p>Loading deployment history...</p>
          ) : !history || history.length === 0 ? (
            <p className="text-muted-foreground">No deployments logged yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date & Time</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Fix #</TableHead>
                  <TableHead>Checkpoint ID</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((deployment) => (
                  <TableRow key={deployment.id}>
                    <TableCell>
                      {new Date(deployment.deployedAt).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </TableCell>
                    <TableCell className="font-mono">{deployment.version}</TableCell>
                    <TableCell className="font-mono">#{deployment.fixNumber}</TableCell>
                    <TableCell className="font-mono text-sm">
                      {deployment.checkpointId || '-'}
                    </TableCell>
                    <TableCell className="max-w-md truncate">
                      {deployment.notes || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      </main>
    </div>
  );
}
